/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Heading, LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import React, { FC, useEffect, useRef } from 'react';

import Lottie from '../../generic-components/Lottie';
import { ReactComponent as PlayIcon } from '../../assets/svg/playButton.svg';

interface IFirstImpressionPageProps {
  children: React.ReactElement[];
}

interface IFirstImpressionPageHeaderProps {
  texts: string[];
  isLoadingState: boolean | undefined;
  onSubmit: () => void;
  buttonText: string;
  buttonId: string;
  buttonDisabled: boolean;
}

const FirstImpressionPageHeader: FC<IFirstImpressionPageHeaderProps> = ({
  texts,
  isLoadingState,
  onSubmit,
  buttonText,
  buttonId,
  buttonDisabled,
}) => (
  <div className='flex flex-col w-2/4 gap-[23px]'>
    <div className='flex flex-col gap-[4px]'>
      {texts.map((text) => (
        <Heading.H3 key={text} title={text}>
          {text}
        </Heading.H3>
      ))}
    </div>

    <LiliButton
      onClick={onSubmit}
      isLoading={isLoadingState}
      className='w-[225px] text-[14px]'
      title={buttonText}
      type={LiliButtonType.PRIMARY}
      id={buttonId}
      data-testid={buttonId}
      disabled={buttonDisabled}
    />
  </div>
);

interface FirstImpressionLottieSectionProps {
  lottie: string;
  onLottieClick?: () => void;
  eventListeners?: { event: string; callback: () => void }[];
  isLoop: boolean;
  finishedState?: boolean;
}

const FirstImpressionLottieSection: FC<FirstImpressionLottieSectionProps> = ({
  lottie,
  onLottieClick,
  eventListeners,
  isLoop,
  finishedState,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (finishedState && containerRef.current && canvasRef.current) {
      const container = containerRef.current;
      const canvas = canvasRef.current;
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'; // Dark overlay with 60% opacity
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    } else if (!finishedState && containerRef.current && canvasRef.current) {
      // clear the canvas
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, [finishedState]);
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className='relative flex flex-col w-[512px] rounded-[16px] gap-[16px]'
      onClick={onLottieClick}
    >
      <div className='relative flex flex-col items-center justify-center'>
        <Lottie
          className='max-w-max max-h-max rounded-[16px]'
          isAutoplay
          isLoop={isLoop}
          lottiePath={lottie}
          eventListeners={eventListeners}
        />
        {finishedState && (
          <>
            <canvas ref={canvasRef} className='absolute top-0 left-0 w-full h-full rounded-[16px]' />
            <div className='flex flex-col  items-center justify-center absolute z-10 cursor-pointer'>
              <PlayIcon />
              <Heading.H8 className='text-text-main-primary-inverse'>Watch Again</Heading.H8>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const FirstImpressionPage: FC<IFirstImpressionPageProps> & {
  Header: typeof FirstImpressionPageHeader;
  LottieSection: typeof FirstImpressionLottieSection;
} = ({ children }) => <div className='flex w-full items-center justify-center'>{children}</div>;

FirstImpressionPage.Header = FirstImpressionPageHeader;
FirstImpressionPage.LottieSection = FirstImpressionLottieSection;
